import * as React from 'react';
import type { DisplayLayoutModeType } from '@sdk/schedule/types/DisplayLayoutModeType';

import type { RowDataType } from '../types/RowType';
import type {
  OpenEventCallback,
  AddEventType,
  IsShiftLineVisibleType,
  ShouldShowPlusType,
  IsSpecialDayType,
} from '../types';
import type { FormattedDateString } from '../types/FormattedDateString';
import EventBox from '../EventBox';
import ShowPlus from '../components/ShowPlus';
import { ToggleEventType, IsEventSelectedType } from '../hooks/useEventSelection';
import useHoverColumns from '../hooks/useHoverColumns';

import ScheduleCell from './ScheduleCell';
import AvailableRow from './AvailableRow';
import UnassignedRow from './UnassignedRow';
import { UNASSIGNED, AVAILABLE } from './constants';

type Props = {
  columns: Array<FormattedDateString>;
  rowData: RowDataType;
  openEvent: OpenEventCallback;
  columnsCount?: number;
  shiftAppearanceMode: DisplayLayoutModeType;
  addEvent: AddEventType;
  toggleEvent: ToggleEventType;
  hasSelectedEvents: boolean;
  isEventSelected: IsEventSelectedType;
  isShiftLineVisible: IsShiftLineVisibleType;
  shouldShowPlus: ShouldShowPlusType;
  isBusinessClosed: IsSpecialDayType;
  isHolidayPay: IsSpecialDayType;
};
const SpecialGroupRegularRow: React.FC<React.PropsWithChildren<Props>> = ({
  columns,
  rowData,
  openEvent,
  columnsCount,
  shiftAppearanceMode,
  addEvent,
  toggleEvent,
  hasSelectedEvents,
  isEventSelected,
  isShiftLineVisible,
  shouldShowPlus,
  isBusinessClosed,
  isHolidayPay,
}) => {
  const { hasNameColumn = true, events, group, type, isLast, eventHeight } = rowData;
  const { isHovered, toggleHover } = useHoverColumns(columns);
  const Row = type === UNASSIGNED ? UnassignedRow : AvailableRow;

  return (
    <Row>
      {hasNameColumn && <ScheduleCell columnsCount={columnsCount} isLast={isLast} />}
      {columns.map((day) => {
        const event = events[day];
        const userForEvent = (group as any).isForDtstart ? null : { id: 0 };
        const toggle = (): void => toggleHover(day);

        const showPlus: boolean =
          isHovered(day) && shouldShowPlus({ user: userForEvent, group, day });

        return (
          <ScheduleCell
            key={day}
            columnsCount={columnsCount}
            isLast={isLast}
            isBusinessClosed={isBusinessClosed({ day })}
            isHolidayPay={isHolidayPay({ day })}
            onMouseEnter={toggle}
            onMouseLeave={toggle}>
            {Boolean(event) && (
              <EventBox
                key={event.id}
                event={event}
                openEvent={openEvent}
                shiftAppearanceMode={shiftAppearanceMode}
                hoverComponent={ScheduleCell}
                toggleEvent={toggleEvent}
                hasSelectedEvents={hasSelectedEvents}
                isEventSelected={isEventSelected}
                isExchange={type === AVAILABLE}
                isShiftLineVisible={isShiftLineVisible}
                height={eventHeight}
                showPlus={showPlus}
                isHovered={isHovered(day)}
              />
            )}

            {showPlus && <ShowPlus day={day} group={group} addEvent={addEvent} event={event} />}
          </ScheduleCell>
        );
      })}
    </Row>
  );
};

export default SpecialGroupRegularRow;
